import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { TypeAnimation } from 'react-type-animation';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import {
  solutionCategories,
  animatedTextsDe,
  animatedTextsEn,
  heroStats,
  benefits,
  platformFeatures,
  services
} from '../data/homePageData';

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

export default function HomePage() {
  const { language } = useLanguage();

  // Prerender guard: TypeAnimation mutates the DOM character-by-character.
  // During Puppeteer prerendering, we must render ONLY static text.
  // After client-side hydration completes, we enable the animation.
  //
  // The key: initial state MUST match what Puppeteer sees, or React will
  // create duplicate content during hydration reconciliation.
  const [showAnimation, setShowAnimation] = useState(false);
  
  useEffect(() => {
    // Only enable animation after hydration completes AND we're not in Puppeteer
    const isHeadless = 
      navigator.webdriver || 
      /HeadlessChrome|Puppeteer/i.test(navigator.userAgent);
    
    if (!isHeadless) {
      // Delay ensures page is fully settled before animation starts
      const timer = setTimeout(() => setShowAnimation(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const animatedTexts = language === 'de' ? animatedTextsDe : animatedTextsEn;
  const firstPhrase = animatedTexts[0];

  const seoDesc = language === 'de'
    ? 'e-Matrix Innovations - Ihr Full-Service E-Learning Anbieter für Arbeitssicherheit, Compliance und Corporate Training in Österreich. Unterweisungssystem, Besucherunterweisung, Cyber Security Awareness. SCORM-kompatibel. Von der Konzeption bis zum Rollout.'
    : 'e-Matrix Innovations - Your full-service e-learning provider for workplace safety, compliance and corporate training in Austria. Training system, visitor instruction, cyber security awareness. SCORM-compatible. From concept to rollout.';

  const seoKeywords = language === 'de'
    ? 'E-Learning Österreich, Arbeitssicherheit E-Learning, Unterweisungssystem, Besucherunterweisung, Cyber Security Awareness, DSGVO Schulung, Compliance Training, SCORM E-Learning, Corporate Training, digitale Unterweisung, Pflichtschulung online, e-Matrix'
    : 'E-Learning Austria, Workplace Safety E-Learning, Training System, Visitor Instruction, Cyber Security Awareness, GDPR Training, Compliance Training, SCORM E-Learning, Corporate Training, Digital Instruction, e-Matrix';

  return (
    <div className="min-h-screen relative" data-testid="home-page">
      <SEO
        title={language === 'de' ? 'Corporate E-Learning für Arbeitssicherheit & Compliance' : 'Corporate E-Learning for Workplace Safety & Compliance'}
        description={seoDesc}
        keywords={seoKeywords}
        path="/"
        lang={language}
      />
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E7F0FA] via-white to-[#dde9f5]" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-[#C5D8EE]/40 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D0E2F2]/30 to-transparent rounded-full blur-3xl" />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div {...fadeUp}>
              {/* Simple Solutions since 2015 Badge */}
              <div className="flex items-center space-x-2 mb-8">
                <span className="text-sm font-semibold tracking-wide text-[#2E5E99]">
                  Simple Solutions
                </span>
                <span className="text-sm text-[#5A7A9B]">
                  {language === 'de' ? 'seit' : 'since'}{' '}
                  <span className="relative inline-block">
                    <span className="font-bold text-[#0D2440]">2015</span>
                    <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M0,8 Q50,0 100,8" stroke="#2E5E99" strokeWidth="2" fill="none" />
                    </svg>
                  </span>
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0D2440] leading-tight mb-4">
                {language === 'de' ? 'Wir ' : 'We '}
                <span className="text-[#2E5E99]">
                  {showAnimation ? (
                    <TypeAnimation
                      key={language}
                      sequence={animatedTexts}
                      wrapper="span"
                      speed={50}
                      repeat={Infinity}
                    />
                  ) : (
                    <span>{firstPhrase}</span>
                  )}
                </span>
              </h1>

              <h2 className="text-xl md:text-2xl text-[#0D2440] font-semibold mb-4">
                Corporate E-Learning
                <br />
                <span className="text-[#5A7A9B] font-normal">
                  {language === 'de' ? '– wir schaffen Bewusstsein' : '– we create awareness'}
                </span>
              </h2>

              <p className="text-lg text-[#5A7A9B] mb-8 max-w-xl">
                {language === 'de'
                  ? 'Als Full-Service E-Learning Anbieter unterstützen wir Sie von der Konzeption bis zum Rollout – mit System, Inhalten und Expertise.'
                  : 'As a full-service e-learning provider, we support you from concept to rollout — with a system, content and expertise.'}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="hero-cta-contact">
                  <Button className="bg-[#0D2440] hover:bg-[#162D4A] text-white h-12 px-8 text-base">
                    {language === 'de' ? 'Kontakt' : 'Contact'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right - Stats Cards */}
            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {heroStats.map((stat, index) => (
                <Link to={stat.link} key={stat.value} className="block">
                  <motion.div
                    className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-[#C8D8E8]/50 hover:shadow-xl transition-shadow cursor-pointer"
                    whileHover={{ y: -5 }}
                    data-testid={`stat-card-${index}`}
                  >
                    <stat.icon className="w-8 h-8 text-[#2E5E99] mb-4" />
                    <div className="text-3xl font-bold text-[#0D2440] mb-1">{stat.value}</div>
                    <div className="text-sm text-[#5A7A9B]">{stat.label[language]}</div>
                  </motion.div>
                </Link>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solutions Categories - 4 Clickable Badges */}
      <section className="py-20 md:py-32 bg-white relative z-10" data-testid="solutions-categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-sm font-semibold tracking-wide text-[#2E5E99] mb-4 block">
              {language === 'de' ? 'Unsere Lösungen' : 'Our Solutions'}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0D2440]">
              {language === 'de' ? 'Was wir bieten' : 'What We Offer'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {solutionCategories.map((solution, index) => (
              <motion.div
                key={solution.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={solution.link}
                  className="group block h-full"
                  data-testid={`solution-badge-${solution.id}`}
                >
                  <div className="bg-white rounded-2xl p-6 h-full border border-[#C8D8E8] hover:border-[#2E5E99] hover:shadow-xl transition-all duration-300 flex flex-col">
                    {/* Icon and Title in same row */}
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-[#2E5E99]/10 rounded-xl flex items-center justify-center group-hover:bg-[#2E5E99] transition-colors flex-shrink-0">
                        <solution.icon className="w-6 h-6 text-[#2E5E99] group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-semibold text-lg text-[#0D2440] group-hover:text-[#2E5E99] transition-colors">
                        {solution.title[language]}
                      </h3>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-[#5A7A9B] mb-4 flex-grow">
                      {solution.description[language]}
                    </p>

                    {/* Subcategories if any */}
                    {solution.subcategories && (
                      <ul className="text-xs text-[#5A7A9B] space-y-1 mb-4">
                        {solution.subcategories[language].map((sub) => (
                          <li key={sub} className="flex items-center">
                            <span className="w-1.5 h-1.5 bg-[#2E5E99] rounded-full mr-2" />
                            {sub}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Action */}
                    <div className="flex flex-col gap-2">
                      {solution.hasCatalog && (
                        <div className="flex items-center text-[#2E5E99] text-sm font-medium">
                          {language === 'de' ? 'Produktkatalog' : 'Product Catalog'}
                          <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      )}
                      {solution.hasDetailPage && (
                        <Link
                          to={solution.detailLink}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center text-cyan-600 hover:text-cyan-500 text-sm font-medium"
                        >
                          {language === 'de' ? 'Systemdetails' : 'System Details'}
                          <ChevronRight className="w-4 h-4 ml-1 hover:translate-x-1 transition-transform" />
                        </Link>
                      )}
                      {!solution.hasCatalog && !solution.hasDetailPage && (
                        <div className="flex items-center text-[#2E5E99] text-sm font-medium">
                          {language === 'de' ? 'Mehr erfahren' : 'Learn more'}
                          <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32 bg-[#E7F0FA] relative z-10" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-sm font-semibold tracking-wide text-[#2E5E99] mb-4 block">
              {language === 'de' ? 'Ihre Vorteile' : 'Your Benefits'}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0D2440]">
              {language === 'de' ? 'Warum e-Matrix?' : 'Why e-Matrix?'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.key}
                className="bg-white rounded-xl p-8 hover:shadow-lg transition-shadow border border-[#C8D8E8]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`benefit-${benefit.key}`}
              >
                <benefit.icon className="w-10 h-10 text-[#2E5E99] mb-4" />
                <h3 className="font-semibold text-xl text-[#0D2440] mb-2">
                  {benefit.title[language]}
                </h3>
                <p className="text-[#5A7A9B]">
                  {benefit.desc[language]}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Section */}
      <section className="py-20 md:py-32 bg-white relative z-10" data-testid="platform-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Content */}
            <motion.div {...fadeUp}>
              <span className="text-sm font-semibold tracking-wide text-[#2E5E99] mb-4 block">
                {language === 'de' ? 'Unser System' : 'Our System'}
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-[#0D2440] mb-6">
                ematrixsys
              </h2>
              <p className="text-lg text-[#5A7A9B] mb-8">
                {language === 'de'
                  ? 'Eine innovative, skalierbare Lernplattform, die entwickelt wurde, um die Lerneffizienz in Ihrem gesamten Unternehmen zu steigern.'
                  : 'An innovative, scalable learning platform designed to enhance learning efficiency across your organization.'}
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {platformFeatures.map((feature) => (
                  <div key={feature.key} className="flex items-start space-x-3">
                    <feature.icon className="w-6 h-6 text-[#2E5E99] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#0D2440]">
                        {feature.title[language]}
                      </h4>
                      <p className="text-sm text-[#5A7A9B]">
                        {feature.desc[language]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right - Image */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/assets/courses/smartphone_tablet_monitor.jpg"
                  alt="e-Matrix E-Learning Platform auf Smartphone, Tablet und Laptop"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-[#0D2440] relative z-10" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-sm font-semibold tracking-wide text-[#2E5E99] mb-4 block">
              {language === 'de' ? 'Unsere Leistungen' : 'Our Services'}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white">
              {language === 'de' ? 'Alles aus einer Hand' : 'All-in-One Solutions'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.key}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:bg-white/10 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-${service.key}`}
              >
                <service.icon className="w-10 h-10 text-[#2E5E99] mb-4" />
                <h3 className="font-semibold text-xl text-white mb-2">
                  {service.title[language]}
                </h3>
                <p className="text-white/70">
                  {service.desc[language]}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#2E5E99] relative z-10" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              {language === 'de' ? 'Bereit für modernes E-Learning?' : 'Ready for Modern E-Learning?'}
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              {language === 'de'
                ? 'Kontaktieren Sie uns noch heute und erfahren Sie, wie wir Ihre Schulungen digitalisieren können.'
                : 'Contact us today and discover how we can digitize your training.'}
            </p>
            <Link to="/contact" data-testid="cta-contact-btn">
              <Button className="bg-white text-[#2E5E99] hover:bg-white/90 h-14 px-10 text-lg font-semibold">
                {language === 'de' ? 'Jetzt kontaktieren' : 'Contact Now'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
